import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useCart } from "@/context/CartContext";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  { k: "first_name", label: "First Name *", req: true, span: 1 },
  { k: "last_name", label: "Last Name *", req: true, span: 1 },
  { k: "street", label: "Street *", req: true, span: 2 },
  { k: "house_no", label: "No. *", req: true, span: 1 },
  { k: "apartment", label: "Apartment / Building (optional)", req: false, span: 1 },
  { k: "postal_code", label: "Postal Code *", req: true, span: 1 },
  { k: "city", label: "City *", req: true, span: 1 },
  { k: "region", label: "Region / State", req: false, span: 1 },
  { k: "country", label: "Country *", req: true, span: 1 },
  { k: "phone", label: "Phone *", req: true, span: 1 },
  { k: "email", label: "Email *", req: true, span: 1 },
  { k: "notes", label: "Notes (optional)", req: false, span: 2 },
];

const INITIAL = FIELDS.reduce((a, f) => ({ ...a, [f.k]: "" }), {});

export const CartDrawer = ({ whatsappNumber }) => {
  const { items, open, setOpen, removeItem, updateQty, total, clear } = useCart();
  const [form, setForm] = useState(INITIAL);
  const [checkout, setCheckout] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [drawerHeight, setDrawerHeight] = useState(null);
  const actionsRef = useRef(null);

  const handleFieldFocus = (idx, el) => {
    setTimeout(() => {
      if (idx >= FIELDS.length - 3) {
        actionsRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      } else {
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 350);
  };

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => setDrawerHeight(vv.height);
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    onResize();
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleOrder = async () => {
    const missing = FIELDS.filter((f) => f.req && !form[f.k].trim());
    if (missing.length) {
      toast.error("Please fill in all required (*) fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/orders", {
        ...form,
        items: items.map((i) => ({ id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
        total,
      });
    } catch (e) {
      // best-effort logging; still continue to WhatsApp
    }
    const lines = items.map((i) => `• ${i.name} x${i.quantity} — €${(i.price * i.quantity).toFixed(2)}`).join("\n");
    const addr = `${form.street} ${form.house_no}${form.apartment ? ", " + form.apartment : ""}, ${form.postal_code} ${form.city}${form.region ? ", " + form.region : ""}, ${form.country}`;
    const msg = `*NEW ORDER — LUXOR*\n\n*Items:*\n${lines}\n\n*Subtotal:* €${total.toFixed(2)}\n*Shipping:* ${total >= 200 ? "FREE" : "To be confirmed"}\n\n*Name:* ${form.first_name} ${form.last_name}\n*Phone:* ${form.phone}\n*Email:* ${form.email}\n*Address:* ${addr}${form.notes ? `\n*Notes:* ${form.notes}` : ""}`;
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
    setSubmitting(false);
    toast.success("Redirecting to WhatsApp to confirm your order.");
    clear();
    setForm(INITIAL);
    setCheckout(false);
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: drawerHeight ? `${drawerHeight}px` : "100dvh" }}
            className="fixed top-0 right-0 z-[70] w-full sm:w-[480px] bg-[#050805] border-l border-[#29FF87]/20 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-6 border-b border-[#29FF87]/20">
              <h3 className="font-grotesk font-bold text-xl tracking-[0.2em] uppercase text-white">{checkout ? "Checkout" : "Your Cart"}</h3>
              <button onClick={() => setOpen(false)} data-testid="close-cart" className="text-zinc-400 hover:text-white transition-colors">
                <X size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {items.length === 0 ? (
                <p className="font-body text-zinc-500 text-sm uppercase tracking-[0.2em] mt-10 text-center">Your cart is empty.</p>
              ) : !checkout ? (
                <div className="space-y-6">
                  {items.map((i) => (
                    <div key={i.id} className="flex items-start justify-between gap-4 border-b border-[#29FF87]/15 pb-6" data-testid={`cart-item-${i.id}`}>
                      <div className="h-20 w-20 shrink-0 rounded-md overflow-hidden border border-[#29FF87]/20 bg-[#0B120B]">
                        {i.image_url && <img src={resolveImage(i.image_url)} alt={i.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-grotesk font-semibold text-sm text-white">{i.name}</h4>
                        <p className="font-grotesk text-xl price-neon mt-1">€{(i.price * i.quantity).toFixed(2)}</p>
                        <div className="flex items-center gap-3 mt-3">
                          <button onClick={() => updateQty(i.id, -1)} className="border border-[#29FF87]/25 h-7 w-7 flex items-center justify-center hover:border-[#29FF87] transition-colors" data-testid={`qty-minus-${i.id}`}><Minus size={12} /></button>
                          <span className="font-body text-sm w-4 text-center">{i.quantity}</span>
                          <button onClick={() => updateQty(i.id, 1)} className="border border-[#29FF87]/25 h-7 w-7 flex items-center justify-center hover:border-[#29FF87] transition-colors" data-testid={`qty-plus-${i.id}`}><Plus size={12} /></button>
                        </div>
                      </div>
                      <button onClick={() => removeItem(i.id)} className="text-zinc-600 hover:text-red-500 transition-colors" data-testid={`remove-${i.id}`}><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  {/* Order summary */}
                  <div className="border border-[#29FF87]/20 rounded-lg p-4 mb-8 bg-[#0B120B]" data-testid="checkout-summary">
                    {items.map((i) => (
                      <div key={i.id} className="flex justify-between items-center font-body text-sm py-1.5">
                        <span className="flex items-center gap-2 text-[#C9F5DA]">
                          {i.image_url && <img src={resolveImage(i.image_url)} alt="" className="h-8 w-8 rounded object-cover border border-[#29FF87]/20" />}
                          {i.name} <span className="text-[#7BC79A]">×{i.quantity}</span>
                        </span>
                        <span className="text-white">€{(i.price * i.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-[#29FF87]/20 mt-2 pt-2">
                      <span className="font-grotesk font-bold uppercase text-sm text-white">Total</span>
                      <span className="font-grotesk font-bold text-lg price-neon">€{total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Fields */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                    {FIELDS.map((f, i) => (
                      <div key={f.k} className={f.span === 2 ? "col-span-2" : "col-span-1"}>
                        <label className="font-body text-[10px] uppercase tracking-[0.25em] price-neon">{f.label}</label>
                        <input
                          type={f.k === "email" ? "email" : f.k === "phone" ? "tel" : "text"}
                          value={form[f.k]}
                          onChange={set(f.k)}
                          onFocus={(e) => handleFieldFocus(i, e.target)}
                          className="w-full bg-transparent border-b border-[#39FF14]/40 focus:border-[#39FF14] outline-none py-2 font-body text-sm price-neon transition-colors"
                          data-testid={`checkout-${f.k}`}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Actions inside scroll area so the keyboard never hides them */}
                  <div ref={actionsRef} className="mt-8 space-y-4 pb-8">
                    {total >= 200 ? (
                      <p className="font-body text-[11px] uppercase tracking-[0.15em] text-[#29FF87] text-center" data-testid="shipping-note">✓ Free shipping unlocked</p>
                    ) : (
                      <p className="font-body text-[11px] uppercase tracking-[0.15em] text-[#7BC79A] text-center" data-testid="shipping-note">Add €{(200 - total).toFixed(2)} more for free shipping</p>
                    )}
                    <button onClick={handleOrder} disabled={submitting} className="w-full bg-[#29FF87] text-[#050805] py-4 font-body text-sm uppercase tracking-[0.25em] font-bold hover:bg-[#5CFFA8] disabled:opacity-50 transition-colors duration-300 glow-box" data-testid="whatsapp-order">
                      {submitting ? "Processing..." : "Complete order via WhatsApp"}
                    </button>
                    <button onClick={() => setCheckout(false)} className="w-full text-zinc-500 py-2 font-body text-xs uppercase tracking-[0.2em] hover:text-white transition-colors" data-testid="back-to-cart">
                      ← Back to cart
                    </button>
                  </div>
                </div>
              )}
            </div>

            {items.length > 0 && !checkout && (
              <div className="px-6 py-6 border-t border-[#29FF87]/20 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-body text-xs uppercase tracking-[0.3em] text-[#7BC79A]">Total</span>
                  <span className="font-grotesk text-3xl price-neon" data-testid="cart-total">€{total.toFixed(2)}</span>
                </div>
                {total >= 200 ? (
                  <p className="font-body text-[11px] uppercase tracking-[0.15em] text-[#29FF87] text-center">✓ Free shipping unlocked</p>
                ) : (
                  <p className="font-body text-[11px] uppercase tracking-[0.15em] text-[#7BC79A] text-center">Add €{(200 - total).toFixed(2)} more for free shipping</p>
                )}
                <button onClick={() => setCheckout(true)} className="w-full bg-[#29FF87] text-[#050805] py-4 font-body text-sm uppercase tracking-[0.25em] font-bold hover:bg-[#5CFFA8] transition-colors duration-300 glow-box" data-testid="proceed-checkout">
                  Proceed to checkout
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
